import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import { useGetProspectByIdQuery } from '../store/hooks';

interface FieldProps {
  label: string;
  value: string | number | boolean | string[] | undefined | null;
}

function Field({ label, value }: FieldProps) {
  let displayValue: string;

  if (value === undefined || value === null || value === '') {
    displayValue = '-';
  } else if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (Array.isArray(value)) {
    displayValue = value.length > 0 ? value.join(', ') : '-';
  } else {
    displayValue = String(value);
  }

  return (
    <div className="py-2">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{displayValue}</dd>
    </div>
  );
}

export default function ProspectDetailPage() {
  const { prospectSegmentId } = useParams<{ prospectSegmentId: string }>();
  const navigate = useNavigate();

  const { data: prospect, isLoading, error } = useGetProspectByIdQuery(
    { prospectSegmentId: prospectSegmentId! },
    { skip: !prospectSegmentId }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading prospect details..." />
        </main>
      </div>
    );
  }

  if (error || !prospect) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading prospect</h2>
            <p className="text-gray-600 mt-2">Unable to fetch prospect details.</p>
            <Button onClick={() => navigate('/admin-v2/prospects')} className="mt-4">
              Back to Prospects
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const isEnriched = !!prospect.specialty_enriched;

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2/prospects" />
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Prospect Details</h1>
          <Chip
            label={isEnriched ? 'Enriched' : 'Pending Enrichment'}
            color={isEnriched ? 'success' : 'warning'}
          />
        </div>

        {/* Input Data Card */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="mb-4">
              Input Data
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <Field label="Organization" value={prospect.organization} />
              <Field label="Name" value={prospect.name} />
              <Field label="Email" value={prospect.email} />
              <Field label="Job Title" value={prospect.job_title} />
              <Field label="Phone Number" value={prospect.phone_number} />
              <Field label="Source" value={prospect.source} />
            </div>
          </CardContent>
        </Card>

        {/* Enriched Data Card */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="mb-4">
              Enriched Data
            </Typography>
            {isEnriched ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <Field label="Specialty" value={prospect.specialty_enriched} />
                <Field label="Location" value={prospect.location} />
                <Field label="Zipcode" value={prospect.zipcode} />
                <Field label="MSO Count" value={prospect.mso_count} />
                <Field label="MSO Platforms" value={prospect.mso_platforms} />
                <Field label="Services/Modalities" value={prospect.services_modalities} />
                <Field label="Insurance Types" value={prospect.insurance_types} />
                <Field label="Insurance Carriers" value={prospect.insurance_carriers} />
                <Field label="Population Focus" value={prospect.population_focus} />
                <Field label="Clinical Focus" value={prospect.clinical_focus} />
                <Field label="Owns Solo Practice" value={prospect.owns_solo_practice} />
                <Field label="In Group Practice" value={prospect.in_group_practice} />
                <Field label="Owns Group Practice" value={prospect.owns_group_practice} />
                <Field label="Group Practice Website" value={prospect.group_practice_website} />
                <Field label="Colleague Count" value={prospect.colleague_count} />
                <Field label="B2C vs B2B" value={prospect.b2c_vs_b2b} />
              </div>
            ) : (
              <Box className="text-center py-8">
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Enrichment data pending...
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Web enrichment runs asynchronously and may take a few minutes to complete.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Metadata Card */}
        <Card>
          <CardContent>
            <Typography variant="h6" className="mb-4">
              Metadata
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <Field label="Prospect ID" value={prospect.prospect_segment_id} />
              <Field label="Requesting User ID" value={prospect.requesting_user_id} />
              <div className="py-2">
                <dt className="text-sm font-medium text-gray-500">Created At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {prospect.created_at ? (
                    <>
                      {formatDateTime(new Date(prospect.created_at))}
                      <span className="text-gray-500 ml-2">
                        ({formatRelativeTime(new Date(prospect.created_at))})
                      </span>
                    </>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
              <div className="py-2">
                <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {prospect.updated_at ? (
                    <>
                      {formatDateTime(new Date(prospect.updated_at))}
                      <span className="text-gray-500 ml-2">
                        ({formatRelativeTime(new Date(prospect.updated_at))})
                      </span>
                    </>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
